import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useHasPayRates } from '@/stores/identity';
import { listPharmacistOnDutyGaps, type PharmacistOnDutyGap } from '@/lib/scheduling';
import { listAuditEvents } from '@/lib/audit';
import type {
  AuditEvent,
  Location,
  Schedule,
  Shift,
  Staff,
  TimeOffRequest,
  JobType,
} from '@/lib/types';

/**
 * Owner Reports page (Day 15.x — Reporting Phase 1, Owner surface).
 *
 * Multi-pharmacy aggregation view. Owners see comparative data across all
 * locations they own. Same time-navigation + shell-caching pattern as
 * Manager Reports. Tiles are aggregations / comparisons rather than
 * operational detail.
 *
 * Tile inventory:
 *   01 · Hours by pharmacy        — bar comparison, current period
 *   02 · Coverage compliance      — % of operating days with pharmacist coverage
 *   03 · Staff utilization        — over / on-target / under counts per pharmacy
 *   04 · Role mix                 — hours by job_type
 *   05 · Time-off summary         — pending/approved/denied per pharmacy
 *   06 · Publishing health        — last publish lead-time vs configured target
 *   07 · Audit activity           — org-wide event volume + recent items
 *   08 · Labor cost (Phase 2)     — locked, premium-only placeholder
 */

type ViewMode = 'month' | 'year';

interface PeriodRange {
  start: string;
  end: string;
  monthStarts: string[];
  label: string;
}

interface OwnerData {
  locations: Location[];
  staff: Staff[];
  shifts: Shift[];
  schedules: Schedule[];
  timeOff: TimeOffRequest[];
  gapsByLocation: Array<{ locationId: string; locationName: string; gaps: PharmacistOnDutyGap[] }>;
  auditEvents: AuditEvent[];
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
function shiftHours(s: Shift): number {
  const [sh, sm] = s.start_time.split(':').map(Number);
  const [eh, em] = s.end_time.split(':').map(Number);
  let mins = eh * 60 + em - (sh * 60 + sm);
  if (mins < 0) mins += 24 * 60;
  mins -= s.break_minutes ?? 0;
  return Math.max(0, mins) / 60;
}
function buildPeriod(viewMode: ViewMode, year: number, month: number): PeriodRange {
  if (viewMode === 'year') {
    const monthStarts = Array.from({ length: 12 }, (_, m) => `${year}-${pad2(m + 1)}-01`);
    return { start: `${year}-01-01`, end: `${year}-12-31`, monthStarts, label: String(year) };
  }
  const start = `${year}-${pad2(month + 1)}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const end = `${year}-${pad2(month + 1)}-${pad2(lastDay)}`;
  const label = new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  return { start, end, monthStarts: [start], label };
}
function periodTarget(perPeriod: number, period: 'week' | 'month', range: PeriodRange): number {
  if (period === 'month') return perPeriod * range.monthStarts.length;
  const startD = new Date(range.start + 'T00:00:00');
  const endD = new Date(range.end + 'T00:00:00');
  const days = Math.round((endD.getTime() - startD.getTime()) / 86400000) + 1;
  return Math.round(perPeriod * (days / 7));
}
function daysInRange(range: PeriodRange): number {
  const startD = new Date(range.start + 'T00:00:00');
  const endD = new Date(range.end + 'T00:00:00');
  return Math.round((endD.getTime() - startD.getTime()) / 86400000) + 1;
}

const ROLE_COLORS: Record<JobType, string> = {
  pharmacist: '#185FA5',
  pharmacy_technician: '#1D9E75',
  pharmacy_assistant: '#BA7517',
  student: '#534AB7',
  manager: '#888780',
  other: '#444441',
};
const ROLE_LABELS: Record<JobType, string> = {
  pharmacist: 'Pharmacist',
  pharmacy_technician: 'Technician',
  pharmacy_assistant: 'Assistant',
  student: 'Student',
  manager: 'Manager',
  other: 'Other',
};

export default function OwnerReports() {
  const today = useMemo(() => new Date(), []);
  const hasPayRates = useHasPayRates();

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedYear, setSelectedYear] = useState<number>(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(today.getMonth());
  const period = useMemo(
    () => buildPeriod(viewMode, selectedYear, selectedMonth),
    [viewMode, selectedYear, selectedMonth]
  );

  const [initialStatus, setInitialStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [data, setData] = useState<OwnerData>({
    locations: [],
    staff: [],
    shifts: [],
    schedules: [],
    timeOff: [],
    gapsByLocation: [],
    auditEvents: [],
  });

  async function loadPeriodData(locs: Location[]) {
    const shiftsP = supabase
      .from('shifts')
      .select('*')
      .gte('shift_date', period.start)
      .lte('shift_date', period.end)
      .is('cancelled_at', null);
    const schedulesP = supabase
      .from('schedules')
      .select('*')
      .in('month_start_date', period.monthStarts);
    const timeOffP = supabase
      .from('time_off_requests')
      .select('*')
      .lte('start_date', period.end)
      .gte('end_date', period.start)
      .order('start_date');
    const gapsP = Promise.all(
      locs.map(async (loc) => {
        const results = await Promise.allSettled(
          period.monthStarts.map((ms) => listPharmacistOnDutyGaps(loc.id, ms))
        );
        const merged = results
          .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
          .sort((a, b) =>
            a.gap_date.localeCompare(b.gap_date) || a.gap_start.localeCompare(b.gap_start)
          );
        return { locationId: loc.id, locationName: loc.name, gaps: merged };
      })
    );
    const auditP = listAuditEvents(200, 0, null);

    const [shiftsRes, schedRes, toRes, gapsRes, auditRes] = await Promise.all([
      shiftsP, schedulesP, timeOffP, gapsP, auditP,
    ]);
    if (shiftsRes.error) throw shiftsRes.error;
    if (schedRes.error) throw schedRes.error;
    if (toRes.error) throw toRes.error;

    const auditEvents = auditRes.rows.filter((r) => {
      const d = r.occurred_at.slice(0, 10);
      return d >= period.start && d <= period.end;
    });

    return {
      shifts: (shiftsRes.data ?? []) as Shift[],
      schedules: (schedRes.data ?? []) as Schedule[],
      timeOff: (toRes.data ?? []) as TimeOffRequest[],
      gapsByLocation: gapsRes,
      auditEvents,
    };
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [locsRes, staffRes] = await Promise.all([
          supabase.from('locations').select('*').eq('status', 'active').order('name'),
          supabase.from('staff').select('*').is('active_to', null),
        ]);
        if (locsRes.error) throw locsRes.error;
        if (staffRes.error) throw staffRes.error;
        const locations = (locsRes.data ?? []) as Location[];
        const staff = (staffRes.data ?? []) as Staff[];
        const periodSlice = await loadPeriodData(locations);
        if (cancelled) return;
        setData({ locations, staff, ...periodSlice });
        setInitialStatus('loaded');
      } catch (err) {
        if (cancelled) return;
        // Surface via the page banner; setErrMsg already includes err.message.
        setErrMsg((err as Error).message);
        setInitialStatus('error');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialStatus !== 'loaded') return;
    let cancelled = false;
    setRefreshing(true);
    (async () => {
      try {
        const slice = await loadPeriodData(data.locations);
        if (cancelled) return;
        setData((prev) => ({ ...prev, ...slice }));
      } catch (err) {
        // non-critical: period-switch refresh failed. Keep prior data on screen;
        // user can re-pick the period to retry. Don't tear the page down.
        console.warn('OwnerReports period reload failed', err);
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end]);

  function goPrev() {
    if (viewMode === 'year') setSelectedYear((y) => y - 1);
    else if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear((y) => y - 1); }
    else setSelectedMonth((m) => m - 1);
  }
  function goNext() {
    if (viewMode === 'year') setSelectedYear((y) => y + 1);
    else if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear((y) => y + 1); }
    else setSelectedMonth((m) => m + 1);
  }
  function jumpToToday() {
    setSelectedYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
  }

  // ---- derived aggregations ----------------------------------------------

  const hoursByLocation = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of data.shifts) {
      map.set(s.location_id, (map.get(s.location_id) ?? 0) + shiftHours(s));
    }
    return data.locations.map((l) => ({
      location: l,
      hours: map.get(l.id) ?? 0,
    })).sort((a, b) => b.hours - a.hours);
  }, [data.shifts, data.locations]);

  const totalHours = useMemo(
    () => data.shifts.reduce((sum, s) => sum + shiftHours(s), 0),
    [data.shifts]
  );

  /** Coverage compliance per pharmacy = (operating days with ≥1 pharmacist shift) / (operating days in period) */
  const coverageCompliance = useMemo(() => {
    return data.locations.map((loc) => {
      const totalDays = daysInRange(period);
      // operating days = total days minus closed days. Approximate: assume Mon-Sat open, Sun closed.
      // Real check would query location_hours + overrides; this is a quick approximation.
      const startD = new Date(period.start + 'T00:00:00');
      let openDays = 0;
      for (let i = 0; i < totalDays; i++) {
        const d = new Date(startD.getTime() + i * 86400000);
        if (d.getDay() !== 0) openDays++;
      }
      const pharmacistStaffIds = new Set(
        data.staff.filter((s) => s.job_type === 'pharmacist').map((s) => s.id)
      );
      const daysCovered = new Set<string>();
      for (const sh of data.shifts) {
        if (sh.location_id !== loc.id) continue;
        if (sh.staff_id && pharmacistStaffIds.has(sh.staff_id)) {
          daysCovered.add(sh.shift_date);
        }
      }
      const pct = openDays > 0 ? Math.round((daysCovered.size / openDays) * 100) : 0;
      return { location: loc, daysCovered: daysCovered.size, openDays, pct };
    }).sort((a, b) => a.pct - b.pct);
  }, [data.locations, data.shifts, data.staff, period]);

  const utilization = useMemo(() => {
    const hoursByStaff = new Map<string, number>();
    for (const s of data.shifts) {
      if (!s.staff_id) continue;
      hoursByStaff.set(s.staff_id, (hoursByStaff.get(s.staff_id) ?? 0) + shiftHours(s));
    }
    let over = 0, under = 0, ontarget = 0;
    for (const staff of data.staff) {
      const hrs = hoursByStaff.get(staff.id) ?? 0;
      const min = periodTarget(Number(staff.min_hours), staff.hours_target_period, period);
      const max = periodTarget(Number(staff.max_hours), staff.hours_target_period, period);
      if (hrs > max) over++;
      else if (hrs < min) under++;
      else ontarget++;
    }
    return { over, ontarget, under, total: data.staff.length };
  }, [data.shifts, data.staff, period]);

  const roleMix = useMemo(() => {
    const hoursByRole = new Map<JobType, number>();
    for (const sh of data.shifts) {
      const staff = data.staff.find((s) => s.id === sh.staff_id);
      if (!staff) continue;
      const job = staff.job_type;
      hoursByRole.set(job, (hoursByRole.get(job) ?? 0) + shiftHours(sh));
    }
    const total = Array.from(hoursByRole.values()).reduce((a, b) => a + b, 0);
    return Array.from(hoursByRole.entries())
      .map(([role, hours]) => ({ role, hours, pct: total > 0 ? Math.round((hours / total) * 100) : 0 }))
      .sort((a, b) => b.hours - a.hours);
  }, [data.shifts, data.staff]);

  const timeOffByLocation = useMemo(() => {
    return data.locations.map((loc) => {
      const staffAtLoc = new Set(
        data.staff.filter((s) => s.primary_location_id === loc.id).map((s) => s.id)
      );
      const reqs = data.timeOff.filter((t) => staffAtLoc.has(t.staff_id));
      return {
        location: loc,
        pending: reqs.filter((r) => r.status === 'pending').length,
        approved: reqs.filter((r) => r.status === 'approved').length,
        denied: reqs.filter((r) => r.status === 'denied').length,
      };
    });
  }, [data.locations, data.staff, data.timeOff]);

  const publishingHealth = useMemo(() => {
    return data.locations.map((loc) => {
      const targetLead = loc.schedule_publish_lead_days ?? 10;
      // Find published schedules in the period and compute actual lead-days.
      const rows = data.schedules
        .filter((s) => s.location_id === loc.id && s.status === 'published' && s.published_at)
        .map((s) => {
          const monthStart = new Date(s.month_start_date + 'T00:00:00');
          const publishedAt = new Date(s.published_at!);
          const leadMs = monthStart.getTime() - publishedAt.getTime();
          const leadDays = Math.round(leadMs / 86400000);
          return { month: s.month_start_date, leadDays };
        })
        .sort((a, b) => b.month.localeCompare(a.month));
      const latest = rows[0];
      const status: 'on_target' | 'late' | 'no_data' = !latest
        ? 'no_data'
        : latest.leadDays >= targetLead
          ? 'on_target'
          : 'late';
      return { location: loc, targetLead, latest, status };
    });
  }, [data.locations, data.schedules]);

  const laborCostByLocation = useMemo(() => {
    if (!hasPayRates) return [];
    const rateByStaff = new Map<string, number>();
    for (const staff of data.staff) {
      if (staff.hourly_rate != null) {
        rateByStaff.set(staff.id, Number(staff.hourly_rate));
      }
    }
    const costByLoc = new Map<string, { cost: number; missing: number }>();
    for (const sh of data.shifts) {
      if (!sh.staff_id) continue;
      const rate = rateByStaff.get(sh.staff_id);
      const cur = costByLoc.get(sh.location_id) ?? { cost: 0, missing: 0 };
      if (rate != null) {
        cur.cost += shiftHours(sh) * rate;
      } else {
        cur.missing += 1;
      }
      costByLoc.set(sh.location_id, cur);
    }
    return data.locations
      .map((l) => ({
        location: l,
        cost: costByLoc.get(l.id)?.cost ?? 0,
        missingShifts: costByLoc.get(l.id)?.missing ?? 0,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [hasPayRates, data.staff, data.shifts, data.locations]);

  const totalLaborCost = useMemo(
    () => laborCostByLocation.reduce((sum, r) => sum + r.cost, 0),
    [laborCostByLocation]
  );
  const maxLaborCost = Math.max(1, ...laborCostByLocation.map((r) => r.cost));

  const auditByType = useMemo(() => {
    const map = new Map<string, number>();
    for (const e of data.auditEvents) {
      map.set(e.event_type, (map.get(e.event_type) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [data.auditEvents]);

  // ---- render ------------------------------------------------------------

  if (initialStatus === 'loading') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading owner reports...
      </div>
    );
  }
  if (initialStatus === 'error') {
    return (
      <div className="container mx-auto py-10 px-4 text-center text-sm text-red-700">
        Couldn't load reports: {errMsg ?? 'unknown error'}
      </div>
    );
  }

  const isCurrentPeriod =
    viewMode === 'month'
      ? selectedYear === today.getFullYear() && selectedMonth === today.getMonth()
      : selectedYear === today.getFullYear();
  const maxHoursAcrossLocations = Math.max(1, ...hoursByLocation.map((h) => h.hours));

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-muted-foreground">Owner view</p>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              Owner reports
              {refreshing && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700"
                  aria-live="polite"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  Updating...
                </span>
              )}
            </h1>
            <p className="text-sm text-muted-foreground">
              {data.locations.length} pharmac{data.locations.length === 1 ? 'y' : 'ies'} · {period.label}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5 bg-card">
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous period"
                className="rounded px-2 py-1 text-sm hover:bg-muted/40"
              >◀</button>
              <span className="px-2 text-sm font-medium tabular-nums w-[150px] text-center inline-block">
                {period.label}
              </span>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next period"
                className="rounded px-2 py-1 text-sm hover:bg-muted/40"
              >▶</button>
            </div>
            {/* K-10 fix: always render the Today button — disable it when
                on the current period — so toggling on/off doesn't reflow the
                row width as the operator clicks ← → through months. */}
            <button
              type="button"
              onClick={jumpToToday}
              disabled={isCurrentPeriod}
              aria-disabled={isCurrentPeriod}
              className="rounded-md border border-border px-2 py-1 text-xs hover:bg-muted/40 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-transparent"
            >Today</button>
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5 bg-card">
              <button
                type="button"
                onClick={() => setViewMode('month')}
                className={
                  viewMode === 'month'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground'
                    : 'rounded px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/40'
                }
              >Month</button>
              <button
                type="button"
                onClick={() => setViewMode('year')}
                className={
                  viewMode === 'year'
                    ? 'rounded px-2.5 py-1 text-xs font-medium bg-primary text-primary-foreground'
                    : 'rounded px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted/40'
                }
              >Year</button>
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Total hours" value={`${totalHours.toFixed(0)} h`} sub={`${data.staff.length} staff`} />
          <KpiCard
            label="Coverage avg"
            value={`${
              coverageCompliance.length > 0
                ? Math.round(coverageCompliance.reduce((a, c) => a + c.pct, 0) / coverageCompliance.length)
                : 0
            }%`}
            sub="across all pharmacies"
          />
          <KpiCard
            label="Pending time-off"
            value={String(data.timeOff.filter((t) => t.status === 'pending').length)}
            sub="awaiting decision"
            tone={data.timeOff.filter((t) => t.status === 'pending').length > 0 ? 'warning' : 'positive'}
          />
          <KpiCard
            label="Audit events"
            value={String(data.auditEvents.length)}
            sub={`in ${period.label}`}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 01 — Hours by pharmacy */}
          <Tile id="01" label="Comparison" title={`Hours by pharmacy · ${period.label}`}>
            {hoursByLocation.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pharmacies.</p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {hoursByLocation.map((row) => {
                  const pct = (row.hours / maxHoursAcrossLocations) * 100;
                  return (
                    <li key={row.location.id} className="flex items-center gap-2">
                      <span className="w-28 truncate text-muted-foreground">{row.location.name}</span>
                      <div className="flex-1 h-3 bg-muted rounded">
                        <div className="h-full rounded bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-14 text-right tabular-nums">{row.hours.toFixed(0)}h</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Tile>

          {/* 02 — Coverage compliance */}
          <Tile id="02" label="Compliance" title={`Pharmacist coverage · ${period.label}`}>
            {coverageCompliance.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pharmacies.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {coverageCompliance.map((row) => {
                  const tone =
                    row.pct >= 95 ? 'text-emerald-700' :
                    row.pct >= 85 ? 'text-amber-700' :
                    'text-red-700';
                  return (
                    <li key={row.location.id} className="flex justify-between items-center">
                      <span className="truncate">{row.location.name}</span>
                      <span className={`tabular-nums font-medium ${tone}`}>
                        {row.pct}% <span className="text-muted-foreground font-normal">({row.daysCovered}/{row.openDays}d)</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="mt-2 text-[10px] text-muted-foreground">
              ≥ 95% green · 85–95% amber · &lt; 85% red. Operating days approximated as Mon–Sat.
            </p>
          </Tile>

          {/* 03 — Utilization */}
          <Tile id="03" label="Utilization" title={`Staff vs target · ${period.label}`}>
            {utilization.total === 0 ? (
              <p className="text-sm text-muted-foreground">No active staff.</p>
            ) : (
              <>
                <div className="flex gap-1 rounded-md overflow-hidden h-7 mb-2">
                  <div
                    className="bg-emerald-500 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(utilization.ontarget / utilization.total) * 100}%` }}
                    title={`On target: ${utilization.ontarget}`}
                  >{utilization.ontarget > 0 && utilization.ontarget}</div>
                  <div
                    className="bg-amber-400 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(utilization.under / utilization.total) * 100}%` }}
                    title={`Under min: ${utilization.under}`}
                  >{utilization.under > 0 && utilization.under}</div>
                  <div
                    className="bg-red-500 flex items-center justify-center text-[10px] font-medium text-white"
                    style={{ width: `${(utilization.over / utilization.total) * 100}%` }}
                    title={`Over max: ${utilization.over}`}
                  >{utilization.over > 0 && utilization.over}</div>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-emerald-700"><span className="inline-block w-2 h-2 bg-emerald-500 rounded mr-1 align-middle" />On target {utilization.ontarget}</span>
                  <span className="text-amber-700"><span className="inline-block w-2 h-2 bg-amber-400 rounded mr-1 align-middle" />Under {utilization.under}</span>
                  <span className="text-red-700"><span className="inline-block w-2 h-2 bg-red-500 rounded mr-1 align-middle" />Over {utilization.over}</span>
                </div>
                <p className="mt-2 text-[10px] text-muted-foreground">
                  Targets normalize across week / month cadences for the selected period.
                </p>
              </>
            )}
          </Tile>

          {/* 04 — Role mix */}
          <Tile id="04" label="Mix" title={`Role hours · ${period.label}`}>
            {roleMix.length === 0 ? (
              <p className="text-sm text-muted-foreground">No scheduled hours.</p>
            ) : (
              <>
                <div className="flex h-7 rounded-md overflow-hidden mb-2">
                  {roleMix.map((r) => (
                    <div
                      key={r.role}
                      className="flex items-center justify-center text-[10px] font-medium text-white"
                      style={{ width: `${r.pct}%`, backgroundColor: ROLE_COLORS[r.role] }}
                      title={`${ROLE_LABELS[r.role]}: ${r.hours.toFixed(0)}h (${r.pct}%)`}
                    >
                      {r.pct >= 8 && `${r.pct}%`}
                    </div>
                  ))}
                </div>
                <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  {roleMix.map((r) => (
                    <li key={r.role} className="flex items-center justify-between">
                      <span className="truncate">
                        <span
                          className="inline-block w-2 h-2 rounded-sm mr-1.5 align-middle"
                          style={{ backgroundColor: ROLE_COLORS[r.role] }}
                        />
                        {ROLE_LABELS[r.role]}
                      </span>
                      <span className="text-muted-foreground tabular-nums">{r.hours.toFixed(0)}h</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Tile>

          {/* 05 — Time-off summary */}
          <Tile id="05" label="Time-off" title={`Time-off summary · ${period.label}`}>
            {timeOffByLocation.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pharmacies.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal pb-1">Pharmacy</th>
                    <th className="text-right font-normal pb-1">Pending</th>
                    <th className="text-right font-normal pb-1">Approved</th>
                    <th className="text-right font-normal pb-1">Denied</th>
                  </tr>
                </thead>
                <tbody>
                  {timeOffByLocation.map((row) => (
                    <tr key={row.location.id}>
                      <td className="py-0.5 truncate max-w-[140px]">{row.location.name}</td>
                      <td className={`text-right tabular-nums ${row.pending > 0 ? 'text-amber-700 font-medium' : ''}`}>{row.pending}</td>
                      <td className="text-right tabular-nums">{row.approved}</td>
                      <td className="text-right tabular-nums">{row.denied}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Tile>

          {/* 06 — Publishing health */}
          <Tile id="06" label="Operations" title="Publishing health">
            <ul className="space-y-1.5 text-xs">
              {publishingHealth.map((row) => {
                const tone =
                  row.status === 'on_target' ? 'text-emerald-700' :
                  row.status === 'late' ? 'text-red-700' :
                  'text-muted-foreground';
                return (
                  <li key={row.location.id} className="flex justify-between items-center">
                    <span className="truncate">{row.location.name}</span>
                    <span className={`tabular-nums ${tone}`}>
                      {row.latest
                        ? `${row.latest.leadDays}d lead (target ${row.targetLead}d)`
                        : `no recent publish (target ${row.targetLead}d)`}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="mt-2 text-[10px] text-muted-foreground">
              Lead = days between publish and month start. Configured per pharmacy.
            </p>
          </Tile>

          {/* 07 — Audit activity */}
          <Tile id="07" label="Trail" title={`Audit activity · ${period.label}`}>
            {auditByType.length === 0 ? (
              <p className="text-sm text-muted-foreground">No audit events in this period.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {auditByType.map(([type, count]) => (
                  <li key={type} className="flex justify-between">
                    <span className="font-mono text-[10px] truncate max-w-[200px]">{type}</span>
                    <span className="tabular-nums text-muted-foreground">{count}</span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/audit" className="mt-2 inline-block text-xs text-primary underline">
              Full audit log →
            </Link>
          </Tile>

          {/* 08 — Labor cost (unlocked on Pro/Enterprise) */}
          {hasPayRates ? (
            <Tile id="08" label="Pay" title={`Labor cost by pharmacy · ${period.label}`}>
              {laborCostByLocation.length === 0 || totalLaborCost === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No labor cost yet. Set <strong>hourly rates</strong> on staff records to
                  populate this. Each shift × the assigned staff member's rate adds up here.
                </p>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground mb-2">
                    Total: <span className="font-semibold text-foreground">CAD ${totalLaborCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                  <ul className="space-y-1.5 text-xs">
                    {laborCostByLocation.map((row) => {
                      const pct = (row.cost / maxLaborCost) * 100;
                      return (
                        <li key={row.location.id} className="flex items-center gap-2">
                          <span className="w-28 truncate text-muted-foreground">{row.location.name}</span>
                          <div className="flex-1 h-3 bg-muted rounded">
                            <div className="h-full rounded bg-emerald-600" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-20 text-right tabular-nums">
                            ${row.cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {laborCostByLocation.some((r) => r.missingShifts > 0) && (
                    <p className="mt-2 text-[10px] text-amber-700">
                      ⚠ {laborCostByLocation.reduce((s, r) => s + r.missingShifts, 0)} shifts have no pay rate set on the staff member — excluded from total.
                    </p>
                  )}
                </>
              )}
            </Tile>
          ) : (
            <Tile id="08" label="Pro plan" title="Labor cost by pharmacy" locked>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Available on the <strong>Pro plan</strong> (and Enterprise). Set hourly
                  rates on staff records to see a $-cost view per pharmacy here.
                </p>
                <div className="rounded-md border border-dashed border-border p-3 space-y-2 opacity-60">
                  {data.locations.slice(0, 4).map((l) => (
                    <div key={l.id} className="flex items-center gap-2 text-xs">
                      <span className="w-28 truncate text-muted-foreground">{l.name}</span>
                      <div className="flex-1 h-2 bg-muted rounded">
                        <div className="h-full rounded bg-muted-foreground/40" style={{ width: '60%' }} />
                      </div>
                      <span className="w-14 text-right tabular-nums">$ ——</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-800">Pro</span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-800">Enterprise</span>
                </div>
              </div>
            </Tile>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- subcomponents -------------------------------------------------

function KpiCard({
  label, value, sub, tone = 'neutral',
}: { label: string; value: string; sub?: string; tone?: 'neutral' | 'positive' | 'warning' }) {
  const cls =
    tone === 'positive' ? 'bg-emerald-50 text-emerald-900' :
    tone === 'warning'  ? 'bg-amber-50 text-amber-900' :
    'bg-muted/40';
  return (
    <div className={`rounded-md px-3 py-2 ${cls}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function Tile({
  id, label, title, locked, children,
}: { id: string; label: string; title: string; locked?: boolean; children: React.ReactNode }) {
  return (
    <section
      className={
        locked
          ? 'rounded-lg border border-dashed border-border bg-muted/20 p-4 space-y-2'
          : 'rounded-lg border border-border bg-card p-4 space-y-2'
      }
    >
      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {id} · {label}
      </span>
      <h2 className="text-sm font-semibold">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
